import { Label } from "../../../../components/ui/common/label";
import { RadioGroup, RadioGroupItem } from "../../../../components/ui/common/radio-group";
import { DELETE_MAPPING_COPY } from "../constants/deleteMapping.constants";
import { isAllVersionsDisabled, isCurrentVersionDisabled } from "../helpers/deleteMapping.helpers";
import {
  DeleteMappingMode,
  DeleteMappingScope,
  DeleteScopeRadioGroupProps
} from "../types/deleteMapping.types";

const currentVersionDescription = (
  mode: DeleteMappingMode,
  currentVersionNumber: number | undefined
): string => {
  switch (mode) {
    case DeleteMappingMode.NoDefaultVersion:
      return DELETE_MAPPING_COPY.noDefaultVersionDescription;
    case DeleteMappingMode.NoVersionRow:
      return DELETE_MAPPING_COPY.noVersionYetDescription;
    case DeleteMappingMode.LastVersion:
      return DELETE_MAPPING_COPY.onlyVersionDescription;
    case DeleteMappingMode.Choice:
      return DELETE_MAPPING_COPY.currentVersionOptionDescription(currentVersionNumber);
  }
};

/**
 * The scope picker. Presentational -- all state lives in useDeleteMappingDialog.
 *
 * There is no shared FormRadioGroup atom in the UI kit yet (see EditModeRadioGroup, which makes
 * the same call), so this stays local to the feature rather than adding one for a single consumer.
 */
const DeleteScopeRadioGroup = ({
  value,
  onChange,
  currentVersionNumber,
  mode,
  disabled,
  hideAllVersions
}: DeleteScopeRadioGroupProps) => {
  const currentDisabled = isCurrentVersionDisabled(mode);
  const allDisabled = isAllVersionsDisabled(mode);

  return (
    <div className="tw-space-y-2">
      <Label>{DELETE_MAPPING_COPY.scopeLabel}</Label>
      <RadioGroup
        value={value}
        onValueChange={next => onChange(next as DeleteMappingScope)}
        disabled={disabled}
        className="tw-space-y-2"
      >
        <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-border tw-p-3">
          <RadioGroupItem
            value={DeleteMappingScope.CurrentVersion}
            id="delete-scope-current"
            disabled={currentDisabled}
            className="tw-mt-0.5"
          />
          <Label
            htmlFor="delete-scope-current"
            className={`tw-font-normal ${
              currentDisabled ? "tw-cursor-not-allowed tw-opacity-60" : "tw-cursor-pointer"
            }`}
          >
            {DELETE_MAPPING_COPY.currentVersionOptionLabel}
            <span className="tw-mt-0.5 tw-block tw-text-xs tw-text-muted-foreground">
              {currentVersionDescription(mode, currentVersionNumber)}
            </span>
          </Label>
        </div>
        {hideAllVersions ? null : (
          <div className="tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-border tw-p-3">
            <RadioGroupItem
              value={DeleteMappingScope.AllVersions}
              id="delete-scope-all"
              disabled={allDisabled}
              className="tw-mt-0.5"
            />
            <Label
              htmlFor="delete-scope-all"
              className={`tw-font-normal ${
                allDisabled ? "tw-cursor-not-allowed tw-opacity-60" : "tw-cursor-pointer"
              }`}
            >
              {DELETE_MAPPING_COPY.allVersionsOptionLabel}
              <span className="tw-mt-0.5 tw-block tw-text-xs tw-text-muted-foreground">
                {allDisabled
                  ? DELETE_MAPPING_COPY.onlyVersionAllVersionsDescription
                  : DELETE_MAPPING_COPY.allVersionsOptionDescription}
              </span>
            </Label>
          </div>
        )}
      </RadioGroup>
    </div>
  );
};

export default DeleteScopeRadioGroup;
